import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, MapPin, Mail, Phone, Globe } from "lucide-react";
import { toast } from "sonner";

const inputClass =
  "bg-white border border-[#4A1D96]/20 text-[#1B0F3A] placeholder:text-[#3B2A5B]/40 rounded-none h-12 focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:border-[#F97316]";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    programme: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast.success("Thank you. A Celesta advisor will contact you within 24 hours.");
      setForm({ name: "", company: "", email: "", programme: "", message: "" });
    }, 900);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-[#FFF8F1] border-t border-[#4A1D96]/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left */}
        <div className="lg:col-span-5 reveal">
          <span className="uppercase tracking-[0.28em] text-xs text-[#F97316] font-semibold">
            Enquiries
          </span>
          <h2
            data-testid="contact-heading"
            className="mt-4 font-serif text-4xl sm:text-5xl leading-[1.05] text-[#1B0F3A]"
          >
            Speak with a
            <br />
            <span className="brand-gradient italic font-medium">
              travel advisor.
            </span>
          </h2>
          <p className="mt-6 text-[#3B2A5B] leading-relaxed max-w-md">
            Tell us where the calendar takes you next. A named advisor will
            respond within one business day — often sooner.
          </p>

          <div className="mt-12 space-y-6 text-[#1B0F3A]">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#4A1D96] text-white">
                <MapPin size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#3B2A5B]/60 font-semibold">
                  Head Office
                </div>
                <div className="mt-1">
                  No 17, St Thomas Street, Mudichur, Chennai 600048
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#4A1D96] text-white">
                <Phone size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#3B2A5B]/60 font-semibold">
                  Concierge · 24/7
                </div>
                <a
                  href="tel:+917338745559"
                  className="mt-1 block hover:text-[#F97316] transition-colors font-medium"
                  data-testid="contact-phone-link"
                >
                  +91 73387 45559
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#4A1D96] text-white">
                <Mail size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#3B2A5B]/60 font-semibold">
                  Email
                </div>
                <a
                  href="mailto:bookings@celestaflights.com"
                  className="mt-1 block hover:text-[#F97316] transition-colors font-medium"
                  data-testid="contact-email-link"
                >
                  bookings@celestaflights.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#4A1D96] text-white">
                <Globe size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#3B2A5B]/60 font-semibold">
                  Website
                </div>
                <a
                  href="https://www.celestaflights.com"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 block hover:text-[#F97316] transition-colors font-medium"
                  data-testid="contact-website-link"
                >
                  www.celestaflights.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="lg:col-span-7 reveal">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="bg-white border border-[#4A1D96]/15 p-8 sm:p-10 space-y-6 shadow-[0_30px_80px_-40px_rgba(74,29,150,0.3)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="text-xs uppercase tracking-[0.2em] text-[#3B2A5B]/70 font-semibold"
                >
                  Full name
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={onChange("name")}
                  className={inputClass}
                  placeholder="Priya Iyer"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="company"
                  className="text-xs uppercase tracking-[0.2em] text-[#3B2A5B]/70 font-semibold"
                >
                  Company (optional)
                </Label>
                <Input
                  id="company"
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={onChange("company")}
                  className={inputClass}
                  placeholder="Company Name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-xs uppercase tracking-[0.2em] text-[#3B2A5B]/70 font-semibold"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={onChange("email")}
                  className={inputClass}
                  placeholder="priya@company.com"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#3B2A5B]/70 font-semibold">
                  I&apos;m interested in
                </Label>
                <Select
                  value={form.programme}
                  onValueChange={onChange("programme")}
                >
                  <SelectTrigger
                    data-testid="contact-select-programme"
                    className={`${inputClass} text-left`}
                  >
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#4A1D96]/20 text-[#1B0F3A] rounded-none">
                    <SelectItem value="corporate">
                      Corporate Travel Management
                    </SelectItem>
                    <SelectItem value="executive">Executive Premium</SelectItem>
                    <SelectItem value="leisure">
                      Holiday / Leisure booking
                    </SelectItem>
                    <SelectItem value="events">MICE / Events</SelectItem>
                    <SelectItem value="other">Something else</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="message"
                className="text-xs uppercase tracking-[0.2em] text-[#3B2A5B]/70 font-semibold"
              >
                Tell us about your travel needs
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={onChange("message")}
                rows={5}
                className={`${inputClass} h-auto py-3`}
                placeholder="Routes, dates, group size, and any constraints…"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="group inline-flex items-center justify-center gap-3 bg-[#F97316] text-white px-8 py-4 text-sm uppercase tracking-[0.22em] font-semibold hover:bg-[#4A1D96] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Sending…" : "Send Enquiry"}
              <Send size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
